import { createStore } from '@nile/mangifera/src/StoreManager/store/createStore';

const classicViewStoreConfig = {
  id: 'classicViewStore',
  initialState: {
    globalStateConditions: {},
    selectedItems: {},
    listData: {},
    listInfo: {},
    searchConditions: {},
    filterConditions: {},
    sortConditions: {},
    selectedRecords: []
  },
  methods: (set, get) => ({
    // Setter for global state conditions
    setGlobalStateConditions: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allConditions = get().globalStateConditions || {};
      const moduleConditions = allConditions?.[module] || {};
      const updatedConditions = {
        ...allConditions,
        [module]: {
          ...moduleConditions,
          [viewId]: data
        }
      };
      set({ globalStateConditions: updatedConditions });
    },

    // Setter for selected items
    setSelectedItem: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allSelectedItems = get().selectedItems || {};
      const moduleSelectedItems = allSelectedItems?.[module] || {};
      const updatedSelectedItems = {
        ...allSelectedItems,
        [module]: {
          ...moduleSelectedItems,
          [viewId]: data
        }
      };
      set({ selectedItems: updatedSelectedItems });
    },
    // Setter for records
    setRecords: (module, viewId, data) => {
      if (!module || !viewId) return;

      const allRecords = get().records || {};
      const records = allRecords?.[module] || {};
      const updatedRecords = {
        ...allRecords,
        [module]: {
          ...records,
          [viewId]: data
        }
      };

      set({ selectedRecords: updatedRecords });
    },

    // Setter for list data
    setListData: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allListData = get().listData || {};
      const moduleListData = allListData?.[module] || {};
      const updatedListData = {
        ...allListData,
        [module]: {
          ...moduleListData,
          [viewId]: data
        }
      };
      set({ listData: updatedListData });
    },

    // Setter for list info
    setListInfo: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allListInfo = get().listInfo || {};
      const moduleListInfo = allListInfo?.[module] || {};
      const updatedListInfo = {
        ...allListInfo,
        [module]: {
          ...moduleListInfo,
          [viewId]: data
        }
      };
      set({ listInfo: updatedListInfo });
    },

    // Setter for search conditions
    setSearchConditions: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allSearchConditions = get().searchConditions || {};
      const moduleSearchConditions = allSearchConditions?.[module] || {};
      const updatedSearchConditions = {
        ...allSearchConditions,
        [module]: {
          ...moduleSearchConditions,
          [viewId]: data
        }
      };
      set({ searchConditions: updatedSearchConditions });
    },

    // Setter for filter conditions
    setFilterConditions: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allFilterConditions = get().filterConditions || {};
      const moduleFilterConditions = allFilterConditions?.[module] || {};
      const updatedFilterConditions = {
        ...allFilterConditions,
        [module]: {
          ...moduleFilterConditions,
          [viewId]: data
        }
      };
      set({ filterConditions: updatedFilterConditions });
    },

    // Setter for sort conditions
    setSortConditions: (module, viewId, data) => {
      if (!module || !viewId) return;
      const allSortConditions = get().sortConditions || {};
      const moduleSortConditions = allSortConditions?.[module] || {};
      const updatedSortConditions = {
        ...allSortConditions,
        [module]: {
          ...moduleSortConditions,
          [viewId]: data
        }
      };
      set({ sortConditions: updatedSortConditions });
    },

    // Getters
    getGlobalStateConditions: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.globalStateConditions?.[module]?.[viewId];
    },

    getSelectedItem: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.selectedItems?.[module]?.[viewId];
    },

    getListData: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.listData?.[module]?.[viewId];
    },

    getRecords: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.selectedRecords?.[module]?.[viewId] || [];
    },

    getListInfo: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.listInfo?.[module]?.[viewId];
    },

    getSearchConditions: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.searchConditions?.[module]?.[viewId];
    },

    getFilterConditions: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.filterConditions?.[module]?.[viewId];
    },

    getSortConditions: (module, viewId) => {
      if (!module || !viewId) return undefined;
      const state = get();
      return state?.sortConditions?.[module]?.[viewId];
    },

    // Clear all data for a specific module
    clearAllModuleData: (module) => {
      if (!module) return;
      set((state) => {
        const newState = { ...state };
        Object.keys(newState).forEach((key) => {
          if (newState[key]?.[module]) {
            delete newState[key][module];
          }
        });
        return newState;
      });
    },

    // Clear data for a specific module and viewId
    clearModuleWithViewId: (module, viewId) => {
      if (!module || !viewId) return;
      set((state) => {
        const newState = { ...state };
        Object.keys(newState).forEach((key) => {
          if (newState[key]?.[module]?.[viewId]) {
            delete newState[key][module][viewId];
          }
        });
        return newState;
      });
    },

    // Reset all filters for a specific module and view
    resetAllFilters: (module, viewId) => {
      if (!module || !viewId) return;
      const state = get();
      const currentGlobalConditions =
        state.globalStateConditions?.[module]?.[viewId];

      if (currentGlobalConditions) {
        const resetConditions = {
          ...currentGlobalConditions,
          filterConditions: [],
          searchConditions: [],
          conditions: null
        };

        set({
          globalStateConditions: {
            ...state.globalStateConditions,
            [module]: {
              ...state.globalStateConditions[module],
              [viewId]: resetConditions
            }
          },
          filterConditions: {
            ...state.filterConditions,
            [module]: {
              ...state.filterConditions[module],
              [viewId]: null
            }
          },
          searchConditions: {
            ...state.searchConditions,
            [module]: {
              ...state.searchConditions[module],
              [viewId]: null
            }
          }
        });
      }
    }
  }),
  cache: {
    expiryTime: 300,
    strategy: 'memory',
    tabBehavior: 'persist',
    clearOnRefresh: true
  }
};

export const useClassicViewStore = createStore(classicViewStoreConfig);
